import { useHandler } from "@redotech/react-util/hook";
import { Button } from "@redotech/redo-web/components/ui/button";
import { Separator } from "@redotech/redo-web/components/ui/separator";
import { memo } from "react";
import { useNavigate } from "react-router-dom";

export interface CheckoutCancelProps {
  onRetryCheckout?: () => void;
  onContinueShopping?: () => void;
}

export const CheckoutCancel = memo(function CheckoutCancel({
  onRetryCheckout,
  onContinueShopping,
}: CheckoutCancelProps) {
  const navigate = useNavigate();

  const handleRetryCheckout = useHandler(() => {
    if (onRetryCheckout) {
      onRetryCheckout();
    } else {
      navigate("/checkout");
    }
  });

  const handleContinueShopping = useHandler(() => {
    if (onContinueShopping) {
      onContinueShopping();
    } else {
      navigate("/");
    }
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-12 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200">
          <div className="text-center space-y-6">
            {/* Cancel Icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
              <svg
                className="h-8 w-8 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path d="M15 9l-6 6" stroke="currentColor" strokeWidth="2" />
                <path d="M9 9l6 6" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>

            {/* Main Content */}
            <div className="space-y-3">
              <h1 className="text-2xl font-bold text-gray-900">
                Payment cancelled
              </h1>

              <p className="text-base text-gray-600">
                Your payment was cancelled and no charges were made.
              </p>

              <p className="text-sm text-gray-500">
                Your items are still in your cart. You can try again when you're
                ready.
              </p>
            </div>

            <Separator className="my-6" />

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={handleRetryCheckout}
                size="lg"
              >
                Return to Checkout
              </Button>

              <Button
                className="w-full"
                onClick={handleContinueShopping}
                size="lg"
                variant="outline"
              >
                Continue Shopping
              </Button>
            </div>

            <Separator className="my-6" />

            {/* Support Info */}
            <div className="text-center">
              <p className="text-xs text-gray-500">
                Having trouble?{" "}
                <a
                  className="text-blue-600 hover:text-blue-500 underline"
                  href="mailto:support@redo.com"
                >
                  Contact support@redo.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
